import axios from "axios";
import { withSkipGlobalError } from "@/lib/http/skip-global-error";
import { getSacredEntry } from "./utils/registry";
import { getSacredImageUrl, SACRED_IMAGE_BASE } from "./utils/image-urls";
import { setLinkRegistryOverrides } from "./utils/link-registry";
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

const EDITOR_TOKEN_KEY = "vedapath_sacred_editor_token";
const EDITOR_USER_KEY = "vedapath_sacred_editor_user";

export function isSacredAdminMode() {
  try {
    return localStorage.getItem("vedapath_admin") === "true";
  } catch {
    return false;
  }
}

export function setSacredAdminMode(on) {
  localStorage.setItem("vedapath_admin", on ? "true" : "false");
}

export function getEditorAuth() {
  try {
    const token = localStorage.getItem(EDITOR_TOKEN_KEY);
    const user = JSON.parse(localStorage.getItem(EDITOR_USER_KEY) || "null");
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

export function setEditorAuth(token, user) {
  if (token) localStorage.setItem(EDITOR_TOKEN_KEY, token);
  else localStorage.removeItem(EDITOR_TOKEN_KEY);
  if (user) {
    localStorage.setItem(EDITOR_USER_KEY, JSON.stringify(user));
    setSacredAdminMode(true);
  } else {
    localStorage.removeItem(EDITOR_USER_KEY);
  }
}

export function clearEditorAuth() {
  setEditorAuth(null, null);
  localStorage.removeItem(EDITOR_TOKEN_KEY);
}

export function editorHeaders() {
  const h = {};
  const { token } = getEditorAuth();
  if (token) h["X-Sacred-Editor-Token"] = token;
  const adminKey = localStorage.getItem("vedaPath_admin_key");
  if (adminKey) h["X-Admin-Key"] = adminKey;
  const accessToken = localStorage.getItem("vedaPath_token");
  if (accessToken) h.Authorization = `Bearer ${accessToken}`;
  return h;
}

export function assetUrl(type, id) {
  return `${API}/sacred-library/asset/${type}/${id}`;
}

export function resolveImageUrl(type, id, overridePath) {
  if (overridePath?.startsWith("/sacred-library/")) return `${API}${overridePath}`;
  if (overridePath?.startsWith("http") || overridePath?.startsWith("/api/")) return overridePath;
  return getSacredImageUrl(
    type === "deity" ? "deities" : `${type}s`,
    id
  );
}

/** Merge static bundle with published KV override */
export async function fetchMergedSacredEntry(type, id) {
  const base = getSacredEntry(type, id);
  if (!base) return null;

  try {
    const pub = await axios.get(`${API}/sacred-library/published/${type}/${id}`);
    const published = pub.data?.entry;
    const asset = pub.data?.asset_url;
    const assetVersion = pub.data?.asset_updated_at;
    const assetSrc = asset
      ? `${assetUrl(type, id)}${assetVersion ? `?v=${encodeURIComponent(assetVersion)}` : ""}`
      : null;
    if (published || assetSrc) {
      const img = assetSrc
        ? assetSrc
        : published?.image_url?.startsWith("/sacred-library/")
          ? `${API}${published.image_url}`
          : published?.image_url ?? base.image_url;
      return {
        ...base,
        ...(published || {}),
        image_url: img,
      };
    }
  } catch (_) {}

  return { ...base };
}

export async function fetchPublishedOverrides() {
  try {
    const res = await axios.get(`${API}/sacred-library/published-overrides?t=${Date.now()}`);
    return res.data?.overrides ?? [];
  } catch (_) {
    return [];
  }
}

export async function loadLinkRegistryFromApi() {
  try {
    const res = await axios.get(`${API}/sacred-library/link-registry`);
    if (res.data?.entries?.length) {
      setLinkRegistryOverrides(res.data.entries);
      return res.data.entries;
    }
  } catch (_) {}
  return null;
}

export async function loginSacredEditor(email, password) {
  const res = await axios.post(
    `${API}/sacred-library/auth/login`,
    { email, password },
    withSkipGlobalError()
  );
  setEditorAuth(res.data.token, res.data.user);
  return res.data;
}

/** Refresh role/email from server (fixes stale localStorage after bootstrap). */
export async function fetchSacredEditorMe() {
  const res = await axios.get(`${API}/sacred-library/auth/me`, {
    ...withSkipGlobalError(),
    headers: editorHeaders(),
  });
  const { token } = getEditorAuth();
  if (token && res.data?.user) {
    setEditorAuth(token, res.data.user);
  }
  return res.data?.user;
}

export async function fetchEditorEntry(type, id) {
  const res = await axios.get(
    `${API}/sacred-library/entry/${type}/${id}`,
    withSkipGlobalError({ headers: editorHeaders() })
  );
  return res.data;
}

export async function saveEditorEntry(type, id, payload) {
  const res = await axios.put(
    `${API}/sacred-library/entry/${type}/${id}`,
    payload,
    withSkipGlobalError({ headers: editorHeaders() })
  );
  return res.data;
}

export async function submitEditorEntry(type, id, payload) {
  const res = await axios.post(
    `${API}/sacred-library/entry/${type}/${id}/submit`,
    payload,
    withSkipGlobalError({ headers: editorHeaders() })
  );
  return res.data;
}

export async function uploadEntryImage(type, id, dataBase64, mime) {
  const res = await axios.post(
    `${API}/sacred-library/entry/${type}/${id}/image`,
    { data_base64: dataBase64, mime },
    withSkipGlobalError({ headers: editorHeaders() })
  );
  return res.data;
}

export async function fetchBootstrapStatus() {
  const res = await axios.get(`${API}/admin/bootstrap/status`, withSkipGlobalError());
  return res.data;
}

/** One-time setup: set admin password using Worker ADMIN_API_KEY (no login required). */
export async function bootstrapSetPassword(email, password, adminApiKey) {
  const res = await axios.post(
    `${API}/admin/bootstrap/set-password`,
    { email, password, role: "admin" },
    withSkipGlobalError({ headers: { "X-Admin-Key": adminApiKey } })
  );
  return res.data;
}

export async function runSacredBootstrap(email, password, adminApiKey) {
  const res = await axios.post(
    `${API}/admin/bootstrap`,
    {
      email,
      password,
      role: "admin",
      approval_required: false,
      can_publish_directly: true,
    },
    withSkipGlobalError({ headers: { "X-Admin-Key": adminApiKey } })
  );
  return res.data;
}

export async function fetchAdminUsers() {
  const res = await axios.get(`${API}/admin/users`, withSkipGlobalError({ headers: editorHeaders() }));
  return { users: res.data.users, needs_bootstrap: res.data.needs_bootstrap };
}

export async function saveAdminUser(user) {
  const res = await axios.put(`${API}/admin/users`, user, withSkipGlobalError({ headers: editorHeaders() }));
  return res.data.user;
}

export async function createAdminUser(user) {
  const res = await axios.post(`${API}/admin/users`, user, withSkipGlobalError({ headers: editorHeaders() }));
  return res.data.user;
}

export async function fetchApprovals() {
  const res = await axios.get(`${API}/admin/approvals`, withSkipGlobalError({ headers: editorHeaders() }));
  return res.data.approvals;
}

export async function reviewApproval(id, action) {
  const res = await axios.post(
    `${API}/admin/approvals/${id}/${action}`,
    {},
    withSkipGlobalError({ headers: editorHeaders() })
  );
  return res.data;
}

export { API, SACRED_IMAGE_BASE };
